if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        this.checkDevice = this.checkDevice.bind(this);
        this.handleSelectChange = this.handleSelectChange.bind(this);
        document.body.classList.add('sticky-atc-bar-enabled');
      }

      get variantIdSelect() {
        return this.querySelector('[name="id"]');
      }

      connectedCallback() {
        this.releaseConnections();
        this.productFormActions = document.querySelector('.main-product-form');
        this.mainProductInfo = document.querySelector('product-info');
        this.container = this.closest('.sticky-atc-bar');
        this.select = this.querySelector('select');
        if (!this.container || !this.select) return;

        this.variantData = this.getVariantData();

        this.init();
        this.abortController = new AbortController();
        this.select.addEventListener('change', this.handleSelectChange, {
          signal: this.abortController.signal,
        });
      }

      disconnectedCallback() {
        this.releaseConnections();
        if (!document.querySelector('sticky-atc-bar')) {
          document.body.classList.remove('sticky-atc-bar-enabled');
          document.documentElement.style.removeProperty('--sticky-atc-bar-height');
          document.documentElement.removeAttribute('data-sticky-atc-bar-visible');
        }
      }

      releaseConnections() {
        this.abortController?.abort();
        this.abortController = undefined;
        this.observer?.disconnect();
        this.observer = undefined;
        if (this.mediaQueryList?.onchange === this.checkDevice) {
          this.mediaQueryList.onchange = null;
        }
        this.mediaQueryList = undefined;
        this.variantChangeUnsubscriber?.();
        this.variantChangeUnsubscriber = undefined;
        clearTimeout(this.variantDispatchTimer);
        this.variantDispatchTimer = undefined;
        this.isUpdating = false;
      }

      handleSelectChange() {
        if (this.isUpdating) return;
        this.isUpdating = true;

        this.mainVariantSelects = this.mainProductInfo?.querySelector('ml-product-options');
        const selectedVariantId = this.variantIdSelect.value;
        this.currentVariant = this.variantData.find((variant) => variant.id === Number(selectedVariantId));

        if (!this.currentVariant) {
          this.isUpdating = false;
          this.updateButton(true, '');
          return;
        }

        if (this.mainVariantSelects) {
          Array.from(this.mainVariantSelects.querySelectorAll('select, fieldset'), (element, index) => {
            const variantOptionVal = this.currentVariant.options[index];
            switch (element.tagName) {
              case 'SELECT': {
                const options = element.querySelectorAll('option');
                options.forEach((option) => option.removeAttribute('selected'));

                element.value = variantOptionVal;
                const selectedOption = element.querySelector(`option[value="${variantOptionVal}"]`);
                if (selectedOption) selectedOption.setAttribute('selected', 'selected');
                break;
              }
              case 'FIELDSET':
                element.querySelectorAll('input').forEach((radio) => {
                  if (radio.value === variantOptionVal) radio.checked = true;
                });
                break;
            }
          });
          this.variantDispatchTimer = setTimeout(() => {
            this.variantDispatchTimer = undefined;
            if (this.isConnected) this.mainVariantSelects.dispatchEvent(new Event('change'));
            this.isUpdating = false;
          }, 0);
        } else {
          this.isUpdating = false;
        }

        this.updatePrice();
        this.updateButton(!this.currentVariant.available, FoxTheme.variantStrings.soldOut);
      }

      getVariantData() {
        if (this.variantData) return this.variantData;

        const productId = this.dataset.productId;
        const sharedVariants = productId
          ? document.querySelector(
              `ml-product-options[data-product-id="${CSS.escape(productId)}"] [data-product-variants]`
            )
          : null;
        const data = sharedVariants || this.container.querySelector('[type="application/json"]');
        this.variantData = data ? JSON.parse(data.textContent) : [];
        return this.variantData;
      }

      init() {
        if (!this.productFormActions) {
          this.setVisible(true);
          return;
        }
        this.productId = this.dataset.productId;

        this.mediaQueryList = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        this.mediaQueryList.onchange = this.checkDevice;
        this.checkDevice();

        const rootMargin = `${this.productFormActions.offsetHeight}px 0px 0px 0px`;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.setVisible(!entry.isIntersecting);
            });
          },
          { threshold: 1, rootMargin }
        );
        this.observer.observe(this.productFormActions);
        this.syncWithMainProductForm();
      }

      setVisible(visible) {
        // Keep the visual state aligned with inert/aria-hidden even before the
        // deferred sticky-bar stylesheet finishes loading.
        this.container.style.transform = visible
          ? 'translate3d(0, 0, 0)'
          : 'translate3d(0, 100%, 0)';
        this.container.classList.toggle('sticky-atc-bar--show', visible);
        // Keyboard focus and the privacy notice stay clear of the bar while it is shown.
        document.documentElement.toggleAttribute('data-sticky-atc-bar-visible', visible);
        if (visible) this.checkDevice();
        this.container.toggleAttribute('inert', !visible);
        if (visible) {
          this.container.removeAttribute('aria-hidden');
        } else {
          this.container.setAttribute('aria-hidden', 'true');
        }
      }

      checkDevice() {
        // Measure the whole bar: the shown bar's padding is part of what it covers.
        document.documentElement.style.setProperty('--sticky-atc-bar-height', this.container.offsetHeight + 'px');
      }

      updateButton(disable = true, text) {
        const productForm = this.querySelector('.sticky-atc-bar__form');
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        if (addButton) {
          const addButtonText = addButton.querySelector('span');
          if (disable) {
            addButton.setAttribute('disabled', 'disabled');
            if (text && addButtonText) addButtonText.textContent = text;
          } else {
            addButton.removeAttribute('disabled');
            if (addButtonText) addButtonText.textContent = FoxTheme.variantStrings.addToCart;
          }
        }

        const buyNowButton = productForm.querySelector('.custom-buy-now-btn');
        if (buyNowButton && (text || !disable)) {
          const available = !disable;
          buyNowButton.dataset.available = String(available);
          buyNowButton.disabled = !available;
          buyNowButton.setAttribute('aria-disabled', String(!available));
          buyNowButton.textContent = available
            ? buyNowButton.dataset.buyNowText
            : buyNowButton.dataset.soldOutText;
        }
      }

      updatePrice() {
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        if (!priceWrapper) return;
        const unitPrice = unitPriceWrapper?.querySelector('.f-price__unit');

        const { compare_at_price, price, unit_price_measurement } = this.currentVariant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out.
        if (!this.currentVariant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = `<s>${FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat)}</s>`)
          );
        } else {
          compareAtPrice?.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            this.currentVariant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${FoxTheme.Currency.getBaseUnit(this.currentVariant)}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else {
          unitPriceWrapper?.classList.add('hidden');
        }
      }

      syncWithMainProductForm() {
        this.variantChangeUnsubscriber?.();
        this.variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          (event) => {
            const isMainProduct = event.data.sectionId === this.mainProductInfo?.dataset.section;
            if (!isMainProduct) return;
            const variant = event.data.variant;

            this.currentVariant = variant;
            if (!variant) {
              this.updateButton(true, '');
              return;
            }

            const variantInput = this.querySelector('[name="id"]');
            if (variantInput) variantInput.value = variant.id;
            this.updatePrice();
            this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
          }
        );
      }
    }
  );
}
